import React, { useEffect } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { itemIdFromPrettyPath } from '../../utils/prettyRoutes';
import { BookOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import { getRecipe, recipePath } from '../../data/guide/recipes';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { WhereToGet } from '../../components/world/WhereToGet';
import { HowToCraft, UsedToCraft } from '../../components/world/ItemCrafting';
import { skillKindOf, skillNameOf } from '../../utils/skillItems';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { entryPathById, getEntry } from '../../utils/guideIndex';
import { activeItemClasses, itemIdFromKey, itemName, itemPath, nameKey } from '../../utils/worldFormat';
import { derived } from '../../utils/worldQueries';
import { BoxContents } from '../../components/world/BagSourceList';
import { bagsOpenedBy } from '../../utils/bagSources';
import { EXPORT_DATE_LABEL } from '../../data/world/package';
import { DataStatus } from '../../components/world/DataStatus';
import { ItemThumb } from '../../components/world/ItemSprite';
import type { WorldData } from '../../types/world';
export function ItemDetail() {
  const {
    itemKey,
    slug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-2735" data-mp="aza1p2bfbb3l">
      {w => {
      const id = slug ? itemIdFromPrettyPath(pathname) ?? '' : itemIdFromKey(itemKey ?? '');
      if (!w.items.has(id) && !w.variants.has(id)) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2736" data-mp="13iqjepvnzz93">
              <p className="text-white" data-id="element-2737" data-mp="yq75tswz944a">No encontramos ese ítem en los archivos.</p>
              <Link to="/guia/items" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2738" data-mp="2sxrf4vtra2rg">Volver a Ítems y drops</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={itemPath(id)} data-id="element-2739" data-mp="2j8rg5cf977d9" />
            <ItemView w={w} id={id} data-id="element-2740" data-mp="2j7ycikblcxzc" />
          </>;
    }}
    </WorldGate>;
}
function ItemView({
  w,
  id
}: {
  w: WorldData;
  id: string;
}) {
  const variant = w.variants.get(id);
  const baseId = variant?.itemId ?? id;
  const item = w.items.get(baseId);
  const name = itemName(w, id);
  const childVariants = (w.idx.variantsByItem.get(id) ?? []).filter(v => v.id !== id);
  const guideEntryId = derived(w).guideItemByName.get(nameKey(name));
  const guideEntry = guideEntryId ? getEntry(guideEntryId) : undefined;
  const skill = item ? skillNameOf(item) : undefined;
  const [params] = useSearchParams();
  const fromRecipe = getRecipe(params.get('desde') ?? undefined);
  useEffect(() => {
    document.title = `${name} · Ítems · Guía MU Vanguard`;
  }, [name]);
  return <div data-id="element-2741" data-mp="1o0fms4on53qj">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Ítems y drops',
      to: '/guia/items'
    }, {
      label: name
    }]} eyebrow={variant && variant.id !== variant.itemId ? 'Variante de ítem' : 'Ítem'} title={name} media={<ItemThumb id={id} name={name} size="hero" placeholder lazy={false} data-id="element-2743" data-mp="1xrabdfpd0ff5" />} data-id="element-2742" data-mp="1o05xkjbf70lw">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2744" data-mp="1swwc327kto57">
          {item?.requiredLevel !== undefined && <span className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-2745" data-mp="26oavvb9b5tdb">Nivel requerido (configuración): {item.requiredLevel}</span>}
          {variant?.level !== undefined && <span className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-2746" data-mp="2gmqkn7kv4qjn">Nivel de variante: {variant.level}</span>}
          {fromRecipe && <Link to={recipePath(fromRecipe.id)} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#050b14] hover:bg-[#F3E5AB]" data-id="element-2747" data-mp="m9iqli4poiz2">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-2748" data-mp="34jn7fcud6ptn" />
              Volver a {fromRecipe.shortTitle}
            </Link>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2749" data-mp="39h6qcq20bqrx" />
        </div>
        {item && <div className="mt-3" data-id="element-2750" data-mp="3eima8esg7r8q">
            <DataStatus kind="catalog" data-id="element-2751" data-mp="3eicjuq3hri2i" />
          </div>}
        {skill && <p className="mt-3 text-[15px] text-gray-200" data-id="element-2752" data-mp="3iimnvx7q3yeq">
            {skillKindOf(item!)} · enseña el poder <span className="font-semibold text-[#F3E5AB]" data-id="element-2753" data-mp="2lugp3qxhg08d">{skill}</span>
          </p>}
        {item?.availabilityNote && <p className="mt-3 text-sm text-gray-400" data-id="element-2754" data-mp="2qpvvjxt5utur">{item.availabilityNote}</p>}
        {variant?.nameContext && <p className="mt-3 text-[15px] text-gray-200" data-id="element-2755" data-mp="2urqz78ykdjbp">{variant.nameContext}</p>}
        {variant?.nameSource && <details className="mt-2 text-xs text-gray-500" data-id="element-2756" data-mp="232e5q7tgi5uf">
            <summary className="flex min-h-[32px] cursor-pointer list-none items-center hover:text-gray-300" data-id="element-2757" data-mp="2325ok3qawedf">Sobre este nombre</summary>
            <p data-id="element-2758" data-mp="1zkhwq959xi4w">
              Nombre de presentación:{' '}
              {variant.nameSourceUrl ? <a href={variant.nameSourceUrl} target="_blank" rel="noreferrer" className="underline decoration-white/20 underline-offset-4 hover:text-gray-300" data-id="element-2759" data-mp="1uln64l12epa8">{variant.nameSource}</a> : variant.nameSource}
              . Pendiente del nombre exacto en el cliente de Vanguard.
              {variant.exportedName && ` En los archivos exportados figura como “${variant.exportedName}”, un nombre generado automáticamente.`}
            </p>
          </details>}
      </WorldPageHeader>

      {guideEntry && <Link to={entryPathById(guideEntry.id)} className="group mb-8 flex items-center gap-3 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-4 transition-colors duration-150 hover:bg-[#D4AF37]/10" data-id="element-2760" data-mp="16w2llxp9jjyu">
          <BookOpen className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-2761" data-mp="xwz2itq3j4kc" />
          <span className="flex-1" data-id="element-2762" data-mp="swtgbt2lia9n">
            <span className="block font-semibold text-white" data-id="element-2763" data-mp="j8p8h29j58g3">Ficha de la guía: {guideEntry.title}</span>
            <span className="block text-sm text-gray-400" data-id="element-2764" data-mp="o78ut3s1cm8z">Incluye las invasiones aprobadas que lo entregan.</span>
          </span>
          <ArrowRight className="h-4 w-4 text-[#D4AF37] transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-2765" data-mp="9bjjq0oe1tj1" />
        </Link>}

      <div className="space-y-10" data-id="element-2766" data-mp="11ydq5todesdi">
        <WhereToGet w={w} id={id} name={name} data-id="element-2767" data-mp="1208elhyup2mc" />

        <HowToCraft id={id} name={name} data-id="element-2768" data-mp="16y0bkqecrexe" />

        <UsedToCraft id={id} name={name} data-id="element-2769" data-mp="16wp0e5ikv4w9" />

        <BoxContents w={w} bags={bagsOpenedBy(w, id)} data-id="element-2770" data-mp="16zus7silckvo" />

        {activeItemClasses(w, baseId).length > 0 && <WorldSection id="clases" title="Clases que pueden usarlo" data-id="element-2771" data-mp="x7xbk68td0pk">
            <ul className="flex flex-wrap gap-2" data-id="element-2772" data-mp="x6426j6tg2dv">
              {activeItemClasses(w, baseId).map(c => <li key={c} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-2773" data-mp="1kvydgwbhliw0">{w.classNames.get(c) ?? `Clase ${c}`}</li>)}
            </ul>
            <p className="mt-2 text-xs text-gray-500" data-id="element-2774" data-mp="3actz6uzpi4pb">Según la configuración exportada el {EXPORT_DATE_LABEL}.</p>
          </WorldSection>}

        {childVariants.length > 0 && <WorldSection id="variantes" title="Variantes" count={childVariants.length} data-id="element-2775" data-mp="3k3wzvvoq4ho2">
            <ul className="flex flex-wrap gap-2" data-id="element-2776" data-mp="3f6ya1pc3ewvo">
              {childVariants.map(v => <li key={v.id} data-id="element-2777" data-mp="2rgu61guzsv45">
                  <Link to={itemPath(v.id)} className="inline-flex h-10 items-center rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-2778" data-mp="2ri5a7hsco4vs">
                    {v.name}
                  </Link>
                </li>)}
            </ul>
          </WorldSection>}

        {variant && variant.id !== variant.itemId && <p className="text-sm text-gray-400" data-id="element-2779" data-mp="fscpvn0pwc62">
            Ítem base: <Link to={itemPath(variant.itemId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-2780" data-mp="bo4y1gyyqg3m">{itemName(w, variant.itemId)}</Link>
          </p>}

        {item && <Disclosure title="Datos de catálogo" data-id="element-2781" data-mp="2a6zx9xyznnv4">
            <StatList rows={[{
          label: 'Nivel requerido (configuración)',
          value: item.requiredLevel
        }, {
          label: 'Energía requerida (configuración)',
          value: item.requiredEnergy
        }, {
          label: 'Nivel de drop (catálogo)',
          value: item.dropLevel
        }, {
          label: 'Daño mínimo',
          value: item.damageMin
        }, {
          label: 'Daño máximo',
          value: item.damageMax
        }, {
          label: 'Defensa',
          value: item.defense
        }, {
          label: 'Velocidad de ataque',
          value: item.attackSpeed
        }, {
          label: 'Tamaño en inventario',
          value: item.width !== undefined && item.height !== undefined ? `${item.width} × ${item.height}` : undefined
        }, {
          label: 'Drop natural habilitado',
          value: item.naturalDropEnabled === undefined ? undefined : item.naturalDropEnabled ? 'Sí' : 'No'
        }, {
          label: 'ID',
          value: item.id
        }]} data-id="element-2782" data-mp="2f59tmbwg8uib" />
          </Disclosure>}
      </div>
    </div>;
}